import { AxiosError, type AxiosResponse } from 'axios'

function filenameFromDisposition(disposition: string | undefined, fallback: string): string {
    const match = disposition?.match(/filename="([^"]+)"/)
    return match?.[1] ?? fallback
}

/** Trigger a browser download from an axios blob response. */
export function triggerBlobDownload(response: AxiosResponse<Blob>, fallbackFilename: string): void {
    const filename = filenameFromDisposition(
        response.headers['content-disposition'] as string | undefined,
        fallbackFilename,
    )
    const url = URL.createObjectURL(response.data)
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = filename
    anchor.click()
    URL.revokeObjectURL(url)
}

/** Download a CSV export; surfaces JSON error bodies from the API. */
export async function downloadCsvResponse(
    response: AxiosResponse<Blob>,
    fallbackFilename: string,
): Promise<void> {
    const contentType = response.headers['content-type'] as string | undefined
    if (contentType?.includes('application/json')) {
        const text = await response.data.text()
        let message = 'Export failed'
        try {
            const body = JSON.parse(text) as { error?: { message?: string } }
            message = body.error?.message ?? message
        } catch {
            if (text) message = text
        }
        throw new Error(message)
    }
    triggerBlobDownload(response, fallbackFilename)
}

/** Pull a readable message from an axios error (including blob error bodies). */
export async function parseAxiosBlobError(err: unknown): Promise<string> {
    if (!(err instanceof AxiosError) || !err.response?.data) {
        return err instanceof Error ? err.message : 'Export failed'
    }

    const { data, status } = err.response
    if (data instanceof Blob) {
        const text = await data.text()
        try {
            const body = JSON.parse(text) as { error?: { message?: string }; message?: string }
            return body.error?.message ?? body.message ?? text
        } catch {
            return text || `Export failed (${status})`
        }
    }

    const body = data as { error?: { message?: string }; message?: string }
    return body.error?.message ?? body.message ?? err.message
}
